import { Component, Input, OnInit, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators, FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FileUploadModule } from 'primeng/fileupload';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { OnboardingService } from '../../../../services/onboarding/onboarding.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BlockUIModule } from 'primeng/blockui';

import { isFieldInvalid, validateFile } from '../../../../direct-apply/shared/validators/form-validation.utils';

export interface UploadedFileItem {
    file?: File;
    previewUrl: string | SafeResourceUrl;
    name: string;
    type?: string;
    base64Data?: string;
    isExistingUrl?: boolean;
}

@Component({
    selector: 'app-awards-accolades',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SelectButtonModule,
        FileUploadModule,
        DialogModule,
        ButtonModule,
        InputTextModule,
        DropdownModule,
        InputTextareaModule,
        ToastModule,
        ConfirmDialogModule,
        ProgressSpinnerModule,
        BlockUIModule
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './awards-accolades.component.html',
    styleUrls: ['./awards-accolades.component.scss']
})
export class AwardsAccoladesComponent implements OnInit {
    @Input() parentForm!: FormGroup;
    @Output() stepSaved = new EventEmitter<void>();
    @Output() saveFailed = new EventEmitter<void>();
    @Output() stepStatusChanged = new EventEmitter<boolean>();

    @ViewChild('hiddenFileInput') hiddenFileInput!: ElementRef<HTMLInputElement>;

    awardsForm!: FormGroup;
    displayPreviewDialog: boolean = false;
    selectedPreviewItem: UploadedFileItem | null = null;
    safePdfUrl: SafeResourceUrl | null = null;
    isPreviewLoading: boolean = false;

    existingIds: (number | null)[] = [];
    savingStates: { saving: boolean }[] = [];
    isLoading: boolean = false;
    savingInProgress: boolean = false;

    currentUploadIndex: number = -1;

    categoryOptions = [
        { label: 'Sports', value: 1 },
        { label: 'Academic', value: 2 },
        { label: 'Leadership', value: 3 },
        { label: 'Social Work', value: 4 },
        { label: 'Competitions', value: 5 },
        { label: 'Others', value: 6 }
    ];

    levelOptions = [
        { label: 'School / Local', value: 1 },
        { label: 'State / Regional', value: 2 },
        { label: 'National', value: 3 },
        { label: 'International', value: 4 }
    ];

    studentId: string = '';

    constructor(
        private fb: FormBuilder,
        private sanitizer: DomSanitizer,
        private onboardingService: OnboardingService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) { }

    ngOnInit() {
        this.initializeForm();
        this.loadExistingData();
    }

    private initializeForm() {
        this.awardsForm = this.fb.group({
            wantsToShare: [null, Validators.required],
            entries: this.fb.array([])
        });

        this.parentForm.setControl('awards', this.awardsForm);
        this.entries.valueChanges.subscribe(() => this.checkStepStatus());

        this.awardsForm.get('wantsToShare')?.valueChanges.subscribe(wants => {
            if (wants && this.entries.length === 0) {
                this.addEntry();
            }
        });
    }

    private loadExistingData() {
        this.isLoading = true;

        const storedData = localStorage.getItem('account');
        if (storedData) {
            const account = JSON.parse(storedData);
            this.studentId = account.userId || account.id || account.Id || account.sub || '';
        }

        this.onboardingService.getAwards().subscribe({
            next: (res: any) => {
                const list = Array.isArray(res) ? res : (res?.result || []);
                if (list && list.length > 0) {
                    this.awardsForm.get('wantsToShare')?.setValue(true, { emitEvent: false });
                    list.forEach((item: any) => this.addEntry(item));
                } else {
                    this.awardsForm.get('wantsToShare')?.setValue(null);
                }
                this.isLoading = false;
                this.checkStepStatus();
            },
            error: (err) => {
                console.error('Error loading awards:', err);
                this.isLoading = false;
                this.checkStepStatus();
            }
        });
    }

    get wantsToShare(): boolean {
        return this.awardsForm.get('wantsToShare')?.value === true;
    }

    get entries(): FormArray {
        return this.awardsForm.get('entries') as FormArray;
    }

    private createEntryGroup(data?: any): FormGroup {
        return this.fb.group({
            id: [data?.id ?? data?.Id ?? null],
            category: [data?.achievementType ?? data?.AchievementType ?? null, Validators.required],
            awardName: [
                data?.awardName ?? data?.AwardName ?? '',
                [Validators.required, Validators.minLength(2), Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]
            ],
            issuingBody: [data?.issuingBody ?? data?.IssuingBody ?? '', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
            organisation: [data?.organisationOrCause ?? data?.OrganisationOrCause ?? '', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
            level: [data?.achievementLevel ?? data?.AchievementLevel ?? null, Validators.required],
            impact: [data?.impact ?? data?.Impact ?? '', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
            responsibility: [data?.responsibility ?? data?.Responsibility ?? '', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
            keyAchievements: [data?.keyAchievements ?? data?.KeyAchievements ?? '', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
            specify: [data?.otherSpecification ?? data?.OtherSpecification ?? '', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
            description: [
                data?.description ?? data?.Description ?? '',
                [Validators.required, Validators.minLength(10), Validators.maxLength(3000)]
            ],
            cvFiles: [[]]
        });
    }

    addEntry(data?: any) {
        const group = this.createEntryGroup(data);

        if (data?.documentUrl) {
            this.loadExistingFile(group, data.documentUrl);
        }

        this.entries.push(group);
        const id = data?.id ?? data?.Id ?? null;
        this.existingIds.push(id);
        this.savingStates.push({ saving: false });
        this.checkStepStatus();
    }

    private loadExistingFile(group: FormGroup, docUrl: string) {
        if (!docUrl || typeof docUrl !== 'string') return;

        const urlWithoutParams = docUrl.split('?')[0];
        const fileName = urlWithoutParams.split('/').pop() || 'Award_Document';
        const isPdf = urlWithoutParams.toLowerCase().endsWith('.pdf');

        group.get('cvFiles')?.setValue([
            {
                name: fileName,
                previewUrl: docUrl,
                type: isPdf ? 'application/pdf' : 'image/jpeg',
                isExistingUrl: true,
                file: null
            }
        ]);
        group.get('cvFiles')?.setErrors(null);
        group.get('cvFiles')?.markAsTouched();
    }

    removeEntry(index: number) {
        const id = this.existingIds[index];

        const doRemoveInUI = () => {
            if (this.entries.length > 1) {
                this.entries.removeAt(index);
                this.existingIds.splice(index, 1);
                this.savingStates.splice(index, 1);
            } else {
                this.entries.at(0).reset();
                this.existingIds[0] = null;
                this.savingStates[0] = { saving: false };
            }
            this.checkStepStatus();
        };

        this.confirmationService.confirm({
            message: 'Are you sure you want to delete this achievement record?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptIcon: 'pi pi-check',
            rejectIcon: 'pi pi-times',
            acceptButtonStyleClass: 'p-button-primary custom-confirm-yes',
            rejectButtonStyleClass: 'p-button-primary custom-confirm-no',
            accept: () => {
                if (id) {
                    this.onboardingService.deleteAward(id).subscribe({
                        next: () => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Deleted',
                                detail: 'Achievement removed successfully.'
                            });
                            doRemoveInUI();
                        },
                        error: (err) => {
                            console.error('Delete error:', err);
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Failed to delete record from server.'
                            });
                        }
                    });
                } else {
                    doRemoveInUI();
                }
            }
        });
    }

    onFileUpload(event: any, index: number) {
        if (event.files && event.files.length > 0) {
            const file = event.files[0];

            const check = validateFile(file);
            if (!check.valid) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Invalid File',
                    detail: check.message
                });
                return;
            }

            this.processFile(file, index);
        }
    }

    private processFile(file: File, index: number) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const previewUrl = file.type === 'application/pdf'
                ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file))
                : e.target.result;

            this.entries.at(index).get('cvFiles')?.setValue([
                {
                    file: file,
                    previewUrl: previewUrl,
                    name: file.name,
                    type: file.type,
                    base64Data: e.target.result,
                    isExistingUrl: false
                }
            ]);
            this.entries.at(index).get('cvFiles')?.markAsDirty();
            this.entries.at(index).get('cvFiles')?.setErrors(null);
            this.entries.at(index).get('cvFiles')?.markAsTouched();
        };
        reader.onerror = () => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to read file. Please try again.'
            });
        };
        reader.readAsDataURL(file);
    }



    triggerAddMore(index: number) {
        this.currentUploadIndex = index;
        if (this.hiddenFileInput) {
            this.hiddenFileInput.nativeElement.click();
        }
    }

    onHiddenFileChange(event: any) {
        const files = event.target.files;
        if (files && files.length > 0 && this.currentUploadIndex >= 0) {
            this.processFile(files[0] as File, this.currentUploadIndex);
        }
        event.target.value = '';
    }

    removeFileObj(index: number) {
        this.entries.at(index).get('cvFiles')?.setValue([]);
        this.entries.at(index).get('cvFiles')?.markAsTouched();
        this.entries.at(index).get('cvFiles')?.markAsDirty();
        this.entries.at(index).get('cvFiles')?.updateValueAndValidity();
    }

    hasFiles(index: number): boolean {
        return (this.entries.at(index).get('cvFiles')?.value?.length || 0) > 0;
    }

    getUploadedFiles(index: number): UploadedFileItem[] {
        return this.entries.at(index).get('cvFiles')?.value || [];
    }

    openPreview(item: UploadedFileItem) {
        if (!item) return;

        this.selectedPreviewItem = item;
        const type = item.file?.type || item.type;
        this.isPreviewLoading = true;
        this.safePdfUrl = null;

        if (type === 'application/pdf') {
            const url = typeof item.previewUrl === 'string' ? item.previewUrl : null;
            if (url) {
                if (url.startsWith('http')) {
                    const encodedUrl = encodeURIComponent(url);
                    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                        `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`
                    );
                } else {
                    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                }
            } else if (typeof item.previewUrl !== 'string') {
                this.safePdfUrl = item.previewUrl;
            }
        }

        this.displayPreviewDialog = true;
    }

    onPreviewLoad() {
        this.isPreviewLoading = false;
    }

    isString(val: any): boolean {
        return typeof val === 'string';
    }

    getCategoryLabel(categoryValue: number): string {
        const category = this.categoryOptions.find(c => c.value === categoryValue);
        return category?.label || 'Award';
    }

    checkStepStatus() {
        const wantsToShare = this.awardsForm.get('wantsToShare')?.value;
        if (wantsToShare !== true) {
            this.stepStatusChanged.emit(true);
            return;
        }
        const allSaved = this.entries.length > 0 &&
                         this.existingIds.every(id => id !== null) &&
                         this.entries.controls.every(c => !c.dirty);
        this.stepStatusChanged.emit(allSaved);
    }

    saveEntry(index: number) {
        void this.persistEntry(index);
    }

    private persistEntry(index: number): Promise<boolean> {
        const group = this.entries.at(index);
        if (!group) return Promise.resolve(false);

        const existingId = this.existingIds[index] || group.get('id')?.value;

        if (existingId && !group.dirty) {
            this.messageService.add({
                severity: 'info',
                summary: 'No Changes',
                detail: 'No changes detected. Nothing to update.'
            });
            return Promise.resolve(true);
        }

        if (group.invalid) {
            group.markAllAsTouched();
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please fill in all required fields.',
                life: 5000
            });
            return Promise.resolve(false);
        }

        const vals = group.value;

        const awardName = (vals.awardName || '').trim();
        if (!awardName || awardName.length < 2) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Award Name is required and must be at least 2 characters.'
            });
            return Promise.resolve(false);
        }

        const description = (vals.description || '').trim();
        if (!description || description.length < 10) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Description is required and must be at least 10 characters.'
            });
            return Promise.resolve(false);
        }

        const files = vals.cvFiles || [];

        this.savingStates[index].saving = true;
        this.isLoading = true;

        const fd = new FormData();
        const category = vals.category;

        fd.append('AwardRecipientCreator', 'Student');
        fd.append('HasAwardOrAchievements', 'true');
        fd.append('AchievementType', String(category));
        fd.append('AchievementLevel', String(vals.level));
        fd.append('AwardName', awardName);
        fd.append('IssuingBody', category === 1 ? (vals.issuingBody || '') : '');
        fd.append('OrganisationOrCause', category === 4 ? (vals.organisation || '') : '');
        fd.append('Impact', category === 4 ? (vals.impact || '') : '');
        fd.append('Responsibility', category === 3 ? (vals.responsibility || '') : '');
        fd.append('KeyAchievements', category === 3 ? (vals.keyAchievements || '') : '');
        fd.append('OtherSpecification', category === 6 ? (vals.specify || '') : '');
        fd.append('Description', description);
        fd.append('GradingPoint', '0');

        if (files.length === 0) {
            fd.append('RemoveDocument', 'true');
        } else if (files.length > 0) {
            const item = files[0];
            if (item.file instanceof File) {
                fd.append('DocumentUrl', item.file, item.name);
            }
        }

        if (existingId) {
            fd.append('Id', String(existingId));
        }

        if (this.studentId) {
            fd.append('ApplicationUserId', this.studentId);
        }

        const action = existingId
            ? this.onboardingService.updateAward(existingId, fd)
            : this.onboardingService.createAward(fd);

        return new Promise((resolve) => {
            action.subscribe({
                next: (res: any) => {
                    this.savingStates[index].saving = false;
                    this.isLoading = false;
                    const newId = res?.id || res?.Id || res?.result?.id || res?.result?.Id;

                    if (newId && !existingId) {
                        this.existingIds[index] = newId;
                    }

                    group.markAsPristine();

                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: existingId
                            ? 'Achievement updated successfully.'
                            : 'Achievement saved successfully.'
                    });

                    this.checkStepStatus();
                    resolve(true);
                },
                error: (err) => {
                    this.savingStates[index].saving = false;
                    this.isLoading = false;

                    let errorMsg = 'Failed to save achievement. Please try again.';
                    if (err?.error?.message) {
                        errorMsg = err.error.message;
                    } else if (err?.error?.errors) {
                        errorMsg = Object.values(err.error.errors).join(', ');
                    }

                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: errorMsg
                    });

                    console.error('Save error:', err);
                    resolve(false);
                }
            });
        });
    }

    async saveData(): Promise<void> {
        if (this.savingInProgress) return;

        if (!this.wantsToShare) {
            this.stepSaved.emit();
            return;
        }

        const hasInvalidEntry = this.entries.controls.some((entry) => entry.invalid);
        if (hasInvalidEntry) {
            this.entries.controls.forEach((entry) => entry.markAllAsTouched());
            this.saveFailed.emit();
            return;
        }

        this.savingInProgress = true;
        this.isLoading = true;

        const results = await Promise.all(
            this.entries.controls.map((_, i) => this.persistEntry(i))
        );

        this.savingInProgress = false;
        this.isLoading = false;

        if (results.every(Boolean)) {
            this.stepSaved.emit();
        } else {
            this.saveFailed.emit();
        }
    }

hasSecondField(category: number): boolean {
  return [1, 3, 4].includes(category); // Sports, Leadership, Social Work
}
}